package com.partejar.app.core.ui.components

import androidx.compose.animation.core.MutableTransitionState
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.onFocusChanged
import androidx.compose.ui.platform.LocalFocusManager
import androidx.compose.ui.semantics.*
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.input.*
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.partejar.app.core.format.*
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import kotlinx.datetime.LocalTime

internal fun clockTimeInput(digits: String): String = buildString {
    digits.take(4).forEachIndexed { index, char ->
        if (index == 2) append(':')
        append(char)
    }
}

/** Separators are visual only: selection and backspace continue to operate on raw digits. */
internal val ClockTimeMask = VisualTransformation { text ->
    val formatted = clockTimeInput(text.text)
    TransformedText(AnnotatedString(formatted), object : OffsetMapping {
        override fun originalToTransformed(offset: Int) =
            (offset + if (offset > 2) 1 else 0).coerceAtMost(formatted.length)
        override fun transformedToOriginal(offset: Int) =
            (offset - if (offset > 2) 1 else 0).coerceIn(0, text.length)
    })
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PartejarTimeField(value: String, onValueChange: (String) -> Unit, label: String, enabled: Boolean = true, error: String? = null) {
    var pickerOpen by rememberSaveable { mutableStateOf(false) }
    var wasFocused by remember { mutableStateOf(false) }
    var blurred by remember { mutableStateOf(false) }
    val focus = LocalFocusManager.current
    val fieldError = error ?: if ((value.length == 5 || (blurred && value.isNotBlank())) && parseClockTime(value) == null)
        "Informe horas de 00 a 23 e minutos de 00 a 59." else null
    PartejarTextField(
        value = value.filter { it in '0'..'9' },
        onValueChange = { input ->
            if (input.all { it in '0'..'9' || it == ':' } && input.count { it in '0'..'9' } <= 4) {
                blurred = false
                onValueChange(clockTimeInput(input.filter { it in '0'..'9' }))
            }
        },
        modifier = Modifier.onFocusChanged { state ->
            if (wasFocused && !state.isFocused && enabled) {
                blurred = true
                val normalized = normalizeClockTimeOnBlur(value)
                if (normalized != value) onValueChange(normalized)
            }
            wasFocused = state.isFocused
        },
        label = label, enabled = enabled, isError = fieldError != null, placeholder = "HH:mm",
        visualTransformation = ClockTimeMask,
        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
        trailingIcon = {
            PartejarIconButton(onClick = { focus.clearFocus(); pickerOpen = true }, enabled = enabled) {
                Icon(PartejarIcons.Clinical.schedule, "Selecionar horário no relógio")
            }
        }
    )
    fieldError?.let {
        Text(it, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodySmall,
            modifier = Modifier.semantics { liveRegion = LiveRegionMode.Polite })
    }
    if (pickerOpen && enabled) {
        val initial = parseClockTime(value)
        val state = rememberTimePickerState(initialHour = initial?.hour ?: 0, initialMinute = initial?.minute ?: 0, is24Hour = true)
        val visibility = remember { MutableTransitionState(false).apply { targetState = true } }
        var confirmed by remember { mutableStateOf<String?>(null) }
        LaunchedEffect(visibility.isIdle, visibility.currentState) {
            if (visibility.isIdle && !visibility.currentState && !visibility.targetState) {
                confirmed?.let(onValueChange)
                pickerOpen = false
            }
        }
        val dismiss = { visibility.targetState = false }
        val spacing = PartejarTheme.spacing
        Dialog(onDismissRequest = dismiss, properties = DialogProperties(usePlatformDefaultWidth = false)) {
            PartejarVisibility(visibility) {
                Surface(
                    modifier = Modifier.windowInsetsPadding(WindowInsets.safeDrawing)
                        .fillMaxWidth(),
                    shape = MaterialTheme.shapes.extraLarge,
                    color = MaterialTheme.colorScheme.surfaceContainerLow
                ) {
                    Column(Modifier.padding(spacing.space16), verticalArrangement = Arrangement.spacedBy(spacing.space16), horizontalAlignment = Alignment.CenterHorizontally) {
                        Text(label, style = MaterialTheme.typography.titleLarge, modifier = Modifier.fillMaxWidth().semantics { heading() })
                        Text("Selecione a hora e os minutos e confirme o horário abaixo.", style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant, modifier = Modifier.fillMaxWidth())
                        // Installed M3's vertical 24h dial is 256dp and fits the available 288dp at 320dp.
                        // Constrain by the window, not a device width; scroll vertically on short windows.
                        Column(Modifier.weight(1f, fill = false).verticalScroll(rememberScrollState())) {
                            TimePicker(state, layoutType = TimePickerLayoutType.Vertical)
                        }
                        val selectedTime = formatClockTime(LocalTime(state.hour, state.minute))
                        PartejarButton("Usar $selectedTime", onClick = { confirmed = selectedTime; dismiss() },
                            enabled = visibility.targetState, motionEnabled = true,
                            leadingIcon = { Icon(PartejarIcons.Feedback.success, null) })
                        PartejarButton("Cancelar", onClick = dismiss, enabled = visibility.targetState,
                            variant = PartejarButtonVariant.TERTIARY, motionEnabled = true,
                            leadingIcon = { Icon(PartejarIcons.Actions.back, null) })
                    }
                }
            }
        }
    }
}
